<template>
  <div class="ex-res-card-header">
    <!-- Top row: breadcrumb and/or lock message -->
    <div
      v-if="show_breadcrumb || locked"
      class="ex-res-card-header__top-row"
      :class="{ 'ex-res-card-header__top-row--inline': show_breadcrumb && locked }"
    >
      <div v-if="show_breadcrumb" class="ex-res-card-header__breadcrumb">
        <Breadcrumbs
          :breadcrumbsList="props.breadcrumbs"
          @onClickBreadcrumb="handleBreadcrumbClick"
        />
      </div>
      <p
        v-if="locked"
        class="ex-res-card-header__lock-message"
      >
        {{ lockMessage }}
      </p>
    </div>
    <!-- Header: status, name, readiness (EmployeeReadinessHeaderBox or legacy bar), Comment, Menu -->
    <OrgObjectHeader
      type="Responsibility"
      :status="status"
      :code="code"
      :name="name"
      :archived="archived"
      :lockShow="locked"
      :currentPosition="currentResponsibility"
      :dataStream="dataStream"
      :readiness="avg_readiness"
      :last_month_readiness="last_month_readiness"
      :totalChatterCount="totalChatterCount"
      :chatterDrawerOpen="chatterDrawerOpen"
      :showMenu="show_menu"
      :showReadinessBox="readinessVariant === 'headerBox'"
      @update:chatterDrawerOpen="$emit('update:chatterDrawerOpen', $event)"
      @reloadChatterList="$emit('reloadChatterList')"
      @requestMoreCheckpointsChatter="$emit('requestMoreCheckpointsChatter', $event)"
      @optionClick="$emit('optionClick', $event)"
    />
    <!-- Legacy variant: average readiness bar (yellow bar, % on right) below header -->
    <div
      v-if="readinessVariant === 'banner'"
      class="ex-res-card-header__avg-readiness"
    >
      <ResponsibilityAvgReadinessBanner :readiness="validatedReadiness" />
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';
import OrgObjectHeader from "@components/OrgObjectHeader/OrgObjectHeader.vue";
import Breadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import ResponsibilityAvgReadinessBanner from '@components/ResponsibilityAvgReadinessBanner/ResponsibilityAvgReadinessBanner.vue';
const props = defineProps({
  breadcrumbs: {
    type: Array,
    default: () => [
      {
        text: "Responsibilities",
        href: "" // Empty href - navigation handled via backToList emit
      },
      {
        text: "Responsibility",
        href: "",
        disabled: true
      }
    ]
  },
  status: {
    type: String,
    default: ''
  },
  code: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  archived: {
    type: Boolean,
    default: false
  },
  avg_readiness: {
    type: Number,
    default: 0
  },
  last_month_readiness: {
    type: Number,
    default: 0
  },
  dataStream: {
    type: Array,
    default: () => []
  },
  totalChatterCount: {
    type: Number,
    default: 0
  },
  currentResponsibility: {
    type: Object,
    default: () => ({})
  },
  chatterDrawerOpen: {
    type: Boolean,
    default: false
  },
  show_breadcrumb: {
    type: Boolean,
    default: true
  },
  locked: {
    type: Boolean,
    default: false
  },
  lockedTemplateName: {
    type: String,
    default: 'Responsibility Template Name'
  },
  show_menu: {
    type: Boolean,
    default: true
  },
  /** 'headerBox' = EmployeeReadinessHeaderBox (default). 'banner' = legacy yellow AVG Readiness bar. */
  readinessVariant: {
    type: String,
    default: 'headerBox',
    validator: (v) => ['headerBox', 'banner'].includes(v)
  }
});

const emit = defineEmits(['reloadChatterList', 'requestMoreCheckpointsChatter', 'optionClick', 'backToList', 'update:chatterDrawerOpen']);

const handleBreadcrumbClick = (breadcrumb) => {
  emit('backToList', breadcrumb);
};

const validatedReadiness = computed(() => {
  const maxValue = 150; // Defined in ReadinessBar component
  return Math.min(props.avg_readiness, maxValue);
});

const lockMessage = computed(() =>
  `This responsibility is locked and controlled by the global template ${props.lockedTemplateName}`
);
</script>
<style scoped>
.ex-res-card-header__top-row {
  margin-bottom: 8px;
}

.ex-res-card-header__top-row--inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.ex-res-card-header__breadcrumb {
  margin-left: 12px;
}

.ex-res-card-header__lock-message {
  margin: 0;
  font-size: 14px;
  line-height: 1.43;
  color: #344054;
}
</style>

<style>
.ex-res-card-header .employee_availability_icon {
  width: auto;
  margin-top: 1px;
}
.ex-res-card-header .code-text {
  color: #98A2B3;
}
.ex-res-card-header .avg-readiness,
.ex-res-card-header__avg-readiness {
  margin-top: 8px;
  padding: 0px 35px 0px 15px;
}
/* ER-11292: the name is no longer capped by an inline clamp (clamp(10rem, 14vw, 20rem))
   — that ceiling truncated the title well before the space the readiness box leaves.
   OrgObjectHeader now sizes the name column with real flexbox (flex: 1 1 auto;
   min-width: 0) and carries the ER-10088 shrink chain, so the title fills the
   available width and ellipsizes only when it genuinely overflows. */
</style>